import React, { useState, useContext, useEffect, useMemo } from 'react';
import { useRequest } from 'src/hooks';
import moment from 'moment';
import { JCdTransactionsContext } from 'src/views/trans/JCdTrans/useJCdTransContext';
import { useNotif } from 'src/hooks';
import endpoints from 'src/lib/endpoints';
import { useSelector } from 'react-redux';

const CustomReportButtonContext = React.createContext();

export const useCustomReportButton = () => {
  return useContext(CustomReportButtonContext);
};

export const CustomReportButtonProvider = ({ children, data }) => {
  const req = useRequest();
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [reportDefinition, setReportDefinition] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [selectedItems, setSelectedItems] = useState([]);
  const [assigning, setAssigning] = useState(false);
  const [hasExplicitlyAssigned, setHasExplicitlyAssigned] = useState(false);

  const context = useContext(JCdTransactionsContext);
  const notif = useNotif();
  const { userToken, base_url } = useSelector(({ auth }) => auth);

  const {
    caption = '',
    component_settings = {},
    disabled = false,
    isEditableInViewMode = true
  } = data;
  // Use key directly since field property might not be properly passed
  const fieldKey = data?.key || data?.field || data?.fld || 'customRepInTransaction';

  const {
    buttonLabel = 'View Report',
    buttonDisabled = false,
    buttonColor = 'primary',
    buttonVariant = 'contained',
    ixRep,
    filter,
    require_payload = false,
    employee_sub_type,
    item_sub_type,
    allow_unassign = false
  } = component_settings;

  const transType = context?.meta?.transType;
  const isViewMode = transType === 'view';
  const disableActionButton =
    buttonDisabled ||
    disabled ||
    loading ||
    (isViewMode && !isEditableInViewMode);

  const persistedReportData = useMemo(
    () => context?.transactionsData?.kvs?.[fieldKey]?.value,
    [context?.transactionsData?.kvs, fieldKey]
  );

  // Filter report data in view mode to show only persisted/saved items
  const filteredReportData = useMemo(() => {
    if (!isViewMode || !Array.isArray(reportData)) {
      return reportData;
    }

    // In view mode, show only items that were saved in the transaction (persistedReportData)
    // This ensures we show the items that were actually selected and saved, regardless of their original sublink status
    if (!Array.isArray(persistedReportData) || persistedReportData.length === 0) {
      return []; // Show nothing if no items were saved
    }

    // Get the sub_ids of persisted items
    const persistedIds = persistedReportData.map(item => item.sub_id).filter(id => id !== undefined && id !== null);

    const filtered = reportData.filter(item => {
      return persistedIds.includes(item.sub_id);
    });

    return filtered;
  }, [isViewMode, reportData, persistedReportData]);

  const getFieldValue = value => {
    if (
      value &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      Object.prototype.hasOwnProperty.call(value, 'value')
    ) {
      return value.value;
    }

    return value;
  };

  const normalizeToArray = value => {
    if (typeof value === 'string') {
      const normalized = value.trim();

      if (normalized === '') return [];

      try {
        return normalizeToArray(JSON.parse(normalized));
      } catch (error) {
        return [];
      }
    }

    if (Array.isArray(value)) return value;
    if (value === undefined || value === null || value === '') return [];
    return [value];
  };

  const getTransactionFilterValue = key => {
    const [source, field] = key.split('.');
    const {
      transactionsData: {
        journalEntries = [],
        transDetails = {},
        kvs = {}
      } = {}
    } = context;

    let val;
    if (source === 'jv') {
      if (field.startsWith('ixSubAcc')) {
        const firstJE = journalEntries[0];
        const jeValue = getFieldValue(firstJE?.[field]);
        if (jeValue !== undefined && jeValue !== null && jeValue !== '') {
          val = jeValue;
        }
      }

      const detailValue = getFieldValue(transDetails?.[field]);
      if (
        !val &&
        detailValue !== undefined &&
        detailValue !== null &&
        detailValue !== ''
      ) {
        val = detailValue;
      }
    } else if (source === 'kvs') {
      const kvsValue = getFieldValue(kvs?.[field]);
      if (kvsValue !== undefined && kvsValue !== null && kvsValue !== '') {
        val = kvsValue;
      }
    }

    return val;
  };

  const loadReportDefinition = async () => {
    try {
      const defRes = await req.get(endpoints.setup.customReport.settings(ixRep));
      if (defRes.success && defRes.data) {
        setReportDefinition(defRes.data);
      }
    } catch (err) {
      console.error('❌ Failed to fetch report definition:', err);
    }
  };

  const fetchReportData = async reportDef => {
    if (!reportDef) return;

    setLoading(true);
    try {
      const payload = {
        ixRep,
        dt1: moment().format(),
        dt2: moment().format(),
        ixProdCat: 0,
        ixWH: 0,
        ixProdCatSub: 0,
        ixBrch: 0,
        ixSub: 0,
        ixAcc: 0,
        ixSubType: 0,
        ixSubType2: 0,
        ixSubType3: 0,
        ixSubType4: 0,
        ixSubType5: 0,
        ixSubType6: 0,
        ixSubType7: 0,
        ixSubType8: 0,
        ixSubType9: 0,
        ixSubType10: 0
      };

      let valid = true;

      if (filter && Array.isArray(filter)) {
        filter.forEach(filterItem => {
          const { key, payload_key, type } = filterItem;
          const value = getTransactionFilterValue(key);

          if (
            value !== undefined &&
            value !== null &&
            value !== '' &&
            value !== 0
          ) {
            if (type === 'string') {
              payload[payload_key] = String(value);
            } else if (type === 'numeric') {
              payload[payload_key] = Number(value);
            } else {
              payload[payload_key] = value;
            }
          } else {
            if (require_payload) {
              valid = false;
              notif.error(`Required filter value missing: ${key}`);
            }
          }
        });
      }

      // For unassign mode, always filter by the selected sub account
      if (allow_unassign) {
        const linkId = getSelectedLinkEntity();
        if (linkId) {
          payload.ixSubAcc2 = linkId;
        }
      }

      if (!valid) {
        setLoading(false);
        return;
      }

      const res = await req.post(endpoints.dashboard.report, payload);
      if (res.success && res.data) {
        const data = res.data?.data || res.data || [];
        const dataArray = normalizeToArray(data);
        setReportData(dataArray);
        
        // Auto-save fetched report data to KVS only if user hasn't explicitly assigned
        // This ensures that explicit assignments override auto-loaded data
        if (context?.transactionsData?.setKvs && !hasExplicitlyAssigned) {
          context.transactionsData.setKvs(prev => ({
            ...prev,
            [fieldKey]: {
              ...(prev?.[fieldKey] ?? {}),
              value: dataArray
            }
          }));
        }
      } else {
        notif.error('Failed to load report');
      }
    } catch (err) {
      console.error('❌ Failed to load report:', err);
      notif.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const handleClick = async () => {
    if (!ixRep) return;

    setLoading(true);

    try {
      const defRes = await req.get(endpoints.setup.customReport.settings(ixRep));
      if (defRes.success && defRes.data) {
        setReportDefinition(defRes.data);
        await fetchReportData(defRes.data);
      }
    } catch (err) {
      console.error('❌ Failed to fetch report definition:', err);
      notif.error('Failed to load report definition');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const toggleItemSelection = itemId => {
    setSelectedItems(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId);
      } else {
        return [...prev, itemId];
      }
    });
  };

  const clearSelection = () => {
    setSelectedItems([]);
  };

  const getSelectedLinkEntity = () => {
    const { journalEntries = [], transDetails = {} } = context?.transactionsData || {};
    const firstJE = journalEntries[0];
    return getFieldValue(firstJE?.ixSubAcc1 || transDetails?.ixSubAcc1);
  };

  const getSelectedLinkEntityName = () => {
    const { transDetails = {} } = context?.transactionsData || {};
    return getFieldValue(transDetails?.Name) || '';
  };

  const getLinkEntityCaption = () => {
    // Try to get from transaction settings
    const transSubType = context?.meta?.transType?._rm?.subType?.['1']?.caption || '';
    if (transSubType) return transSubType;
    
    // Try to get from component settings
    if (employee_sub_type) return 'Link Entity';
    
    // Fallback to subType caption from data
    return data?.subType?.['1']?.caption || 'Entity';
  };

  const handleAssignItems = async () => {
    const linkId = getSelectedLinkEntity();
    const linkName = getSelectedLinkEntityName();
    const linkCaption = getLinkEntityCaption();
    
    if (!linkId) {
      return { success: false, message: `No ${linkCaption.toLowerCase()} selected` };
    }

    if (selectedItems.length === 0) {
      return { success: true, message: 'No items to assign' };
    }

    // Check if any item ID is undefined
    if (selectedItems.some(id => id === undefined || id === null)) {
      const errorMessage = 'Report data missing item ID. Please include sub_id in report SQL.';
      console.error(errorMessage);
      return { success: false, message: errorMessage };
    }

    setAssigning(true);
    try {
      for (const itemId of selectedItems) {
        const payload = {
          values: {
            ixSubLink2: linkId
          }
        };

        const res = await req.put(
          endpoints.setup.sub.subDetails(itemId),
          payload
        );

        if (!res.success) {
          throw new Error(`Failed to assign item ${itemId}`);
        }
      }

      const successMessage = `Successfully assigned ${selectedItems.length} item(s) to ${linkName || linkCaption}`;
      notif.success(successMessage);

      // Mark that user has explicitly assigned items
      setHasExplicitlyAssigned(true);

      // Save ONLY selected rows into transaction KVS
      const selectedData = reportData.filter(item =>
        selectedItems.includes(item.sub_id)
      );

      if (context?.transactionsData?.setKvs) {
        context.transactionsData.setKvs(prev => ({
          ...prev,
          [fieldKey]: {
            ...(prev?.[fieldKey] ?? {}),
            value: selectedData
          }
        }));

        // Show selected items in table
        setReportData(selectedData);
      }

      clearSelection();
      return { success: true, message: successMessage, filteredData: selectedData };
    } catch (err) {
      console.error('❌ Failed to assign items:', err);
      notif.error('Failed to assign items');
      return { success: false, message: 'Failed to assign items' };
    } finally {
      setAssigning(false);
    }
  };

  const handleUnassignItems = async () => {
    if (selectedItems.length === 0) {
      return { success: true, message: 'No items to unassign' };
    }

    // Check if any item ID is undefined
    if (selectedItems.some(id => id === undefined || id === null)) {
      const errorMessage = 'Report data missing item ID. Please include sub_id in report SQL.';
      console.error(errorMessage);
      return { success: false, message: errorMessage };
    }

    setAssigning(true);
    try {
      for (const itemId of selectedItems) {
        const payload = {
          values: {
            ixSubLink2: null // Clear the assignment
          }
        };

        const res = await req.put(
          endpoints.setup.sub.subDetails(itemId),
          payload
        );

        if (!res.success) {
          throw new Error(`Failed to unassign item ${itemId}`);
        }
      }

      const successMessage = `Successfully cleared assignment for ${selectedItems.length} item(s)`;
      notif.success(successMessage);

      // Mark that user has explicitly unassigned items
      setHasExplicitlyAssigned(true);

      // Save unassigned items into transaction KVS (not remaining items)
      const unassignedData = reportData.filter(item =>
        selectedItems.includes(item.sub_id)
      );

      if (context?.transactionsData?.setKvs) {
        context.transactionsData.setKvs(prev => ({
          ...prev,
          [fieldKey]: {
            ...(prev?.[fieldKey] ?? {}),
            value: unassignedData
          }
        }));

        // Show unassigned items in table
        setReportData(unassignedData);
      }

      clearSelection();
      return { success: true, message: successMessage, filteredData: unassignedData };
    } catch (err) {
      console.error('❌ Failed to unassign items:', err);
      notif.error('Failed to clear assignment');
      return { success: false, message: 'Failed to clear assignment' };
    } finally {
      setAssigning(false);
    }
  };

  useEffect(() => {
    if (persistedReportData !== undefined && persistedReportData !== null) {
      const dataArray = normalizeToArray(persistedReportData);
      // Only set reportData if it's empty or in view mode
      // This prevents overwriting the data after assign/unassign operations
      if (reportData.length === 0 || isViewMode) {
        setReportData(dataArray);
      }
      // Reset hasExplicitlyAssigned when loading persisted data
      // This allows auto-save to work again when data is loaded from saved state
      setHasExplicitlyAssigned(false);
      if (ixRep) {
        loadReportDefinition();
      }
      return;
    }

    setReportData([]);
  }, [persistedReportData, ixRep, isViewMode, reportData.length]);

  // Expose function for external call (e.g., from transaction save)
  const assignSelectedItems = async () => {
    // If allow_unassign is true, call unassign instead of assign
    if (allow_unassign) {
      return await handleUnassignItems();
    }
    return await handleAssignItems();
  };

  const unassignSelectedItems = async () => {
    return await handleUnassignItems();
  };

  // Expose globally for transaction save handler (unique per fieldKey)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Create unique global context property per fieldKey to avoid conflicts
      const globalContextKey = `__customReportContext_${fieldKey}`;
      window[globalContextKey] = {
        assignSelectedItems,
        unassignSelectedItems,
        fieldKey,
        selectedItems,
        allow_unassign,
        assigning
      };
    }
    
    return () => {
      if (typeof window !== 'undefined') {
        const globalContextKey = `__customReportContext_${fieldKey}`;
        delete window[globalContextKey];
      }
    };
  }, [assignSelectedItems, unassignSelectedItems, allow_unassign, handleAssignItems, handleUnassignItems, buttonLabel, fieldKey, selectedItems, assigning]);

  return (
    <CustomReportButtonContext.Provider
      value={{
        loading,
        reportData,
        filteredReportData,
        reportDefinition,
        page,
        rowsPerPage,
        buttonLabel,
        disableActionButton,
        buttonVariant,
        buttonColor,
        handleClick,
        handleChangePage,
        handleChangeRowsPerPage,
        selectedItems,
        toggleItemSelection,
        clearSelection,
        handleAssignItems,
        handleUnassignItems,
        assignSelectedItems,
        unassignSelectedItems,
        assigning,
        allow_unassign,
        getSelectedLinkEntity,
        getSelectedLinkEntityName,
        getLinkEntityCaption,
        isViewMode
      }}
    >
      {children}
    </CustomReportButtonContext.Provider>
  );
};
